import { SearchUser } from 'lib-search-types';
import { ComponentProps, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import {
  HookFormSearchSelect,
  HookFormSearchSelectResult,
} from 'components/hookForm';

import { FormValues } from '../composeEmailModal.types';

import { ContactItemContent } from './contactItemContent';

type ComponontPropsType = Omit<
  ComponentProps<typeof HookFormSearchSelect>['componentProps'],
  'getItemTitleByValue'
>;

type ComposeEmailFieldEmailContactProps = {
  name: 'toEmail' | 'cc' | 'bcc';
  label: string;
  multiple?: boolean;
  required?: boolean;
  componentProps?: ComponontPropsType;
};

const createSearchUser = (input: string): SearchUser => ({
  id: 'suggestion',
  name: input,
  email: input,
  type: 'contact',
  org_id: null,
  phone_number: null,
  avatar_url: null,
  company_id: null,
  default_outbound_number: null,
  anonymized_at: null,
  given_name: null,
  family_name: null,
  has_scim_enabled: false,
  external_id: null,
});

const isSearchUser = (
  result: HookFormSearchSelectResult,
): result is SearchUser => {
  if (result && typeof result === 'object' && 'email' in result) {
    return true;
  }
  return false;
};

export const ComposeEmailFieldEmailContact = ({
  name,
  label,
  multiple = false,
  required = false,
  componentProps,
}: ComposeEmailFieldEmailContactProps): JSX.Element => {
  const { control } = useFormContext<FormValues>();

  const getSuggestions = useCallback(
    (searchTerm: string, results: readonly SearchUser[]) => {
      if (isEmail(searchTerm)) {
        const suggestedUser = createSearchUser(searchTerm);

        const existsInResults = results.some((result) => {
          const hitKeys = Object.keys(result);
          // if any property of the hit matches, we consider it to exist so we don't show a duplicate hit
          for (const key of hitKeys) {
            if (result[key] === searchTerm) {
              return true;
            }
          }
        });

        if (!existsInResults) {
          return [suggestedUser];
        }
      }

      return [];
    },
    [],
  );

  return (
    <HookFormSearchSelect
      control={control}
      name={name}
      label={label}
      rules={
        required
          ? {
              required: 'Please select an email address',
            }
          : undefined
      }
      componentProps={{
        ...componentProps,
        multiple,
        clearable: true,
        placeholder: 'Select or type an email address',
        equalityFn: (a, b) => a?.email === b?.email,
        getItemTitleByValue: (value) =>
          value?.name && value?.email ? (
            <span>
              <strong>{value.name}</strong>
              {` <${value.email}>`}
            </span>
          ) : (
            (value?.email ?? value?.name ?? '')
          ),
      }}
      scope="users"
      initialArg={{
        sortBy: { field: 'display_name', order: 'asc' },
        pageSize: 20,
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ value: 'member' }, { value: 'contact' }],
            },
            {
              field: 'email',
              operator: 'is_not_empty',
              values: [],
            },
          ],
        },
      }}
      getSuggestions={getSuggestions}
      transformValue={(user) => {
        const name = user.name || user.email;
        return {
          email: user.email || '',
          name: name || '',
          id: user.id || undefined,
        };
      }}
      renderSelectionListItemContent={(user) => {
        if (!isSearchUser(user)) {
          return null;
        }

        return (
          <ContactItemContent
            id={user.id}
            name={user.name || ''}
            email={user.email || ''}
            avatarUrl={user.avatar_url ?? undefined}
          />
        );
      }}
    />
  );
};
